import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import '../../Layout.css';

const LeftSidebar = ({ isCollapsed, onToggleCollapse, darkMode }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser, logout } = useUser();

    React.useEffect(() => {
        const handleToggle = () => {
            onToggleCollapse();
        };
        window.addEventListener('toggle-sidebar', handleToggle);
        return () => {
            window.removeEventListener('toggle-sidebar', handleToggle);
        };
    }, [onToggleCollapse]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isActive = (path) => {
        if (path === '/tasks') {
            return location.pathname === '/tasks' || location.pathname === '/task-list';
        }
        if (path === '/tasks/new') {
            return location.pathname === '/tasks/new' || location.pathname === '/new-task';
        }
        return location.pathname.startsWith(path);
    };

    const navItems = [
        {
            path: '/dashboard',
            label: 'Dashboard',
            shortLabel: 'Dash',
            icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
            )
        },
        {
            path: '/tasks',
            label: 'Task List',
            shortLabel: 'Task',
            icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
            )
        },
        {
            path: '/tasks/new',
            label: 'New Task',
            shortLabel: 'New',
            icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
            )
        },
        {
            path: '/calendar',
            label: 'Calendar',
            shortLabel: 'Cal',
            icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            ),
        },
        {
            path: '/teams',
            label: 'Teams',
            shortLabel: 'Team',
            icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 005.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            role: ['Global Admin', 'Operational Admin', 'Department Admin'],
            permission: 'can_manage_teams'
        },

    ];

    const validItems = navItems.filter(item => {
        if (currentUser && item.permission && currentUser[item.permission]) return true;
        return !item.role || (currentUser && item.role.includes(currentUser.role));
    });

    return (
        <>
            {!isCollapsed && (
                <div 
                    className="sidebar-backdrop" 
                    onClick={onToggleCollapse}
                />
            )}
            <div
                className={`
                    ${isCollapsed ? 'w-24' : 'w-64'}
                    h-full flex flex-col border-r relative transition-all duration-300 ease-in-out z-40
                    ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
                `}
            >
            {/* Logo Section REMOVED - now in Header (TopBar) */}

            {/* Collapse Toggle Button - Absolute Positioned HIGHER */}
            <button
                onClick={onToggleCollapse}
                className={`
                    absolute -right-3 -top-12 w-8 h-8 rounded-full border shadow-lg flex items-center justify-center z-[100]
                    ${darkMode ? 'bg-gray-800 border-gray-600 text-gray-300' : 'bg-white border-gray-200 text-gray-500'}
                    hover:scale-110 transition-transform sidebar-toggle
                `}
            >
                <svg
                    className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Navigation Items - min-h-0 is CRITICAL for scrolling vs mt-auto */}
            <nav className="flex-1 min-h-0 py-6 overflow-y-auto">
                <div className="space-y-1 px-3">
                    {validItems.map((item) => {
                        const active = isActive(item.path);
                        return (
                            <div key={item.path}>
                                <button
                                    onClick={() => navigate(item.path)}
                                    className={`
                                        w-full flex items-center rounded-xl transition-all duration-200 group relative
                                        ${isCollapsed ? 'flex-col p-2 gap-1 justify-center' : 'flex-row p-3'}
                                        ${active
                                            ? (darkMode ? 'bg-green-900 text-white shadow-md' : 'bg-brand-light text-brand shadow-sm border-brand') // Active State
                                            : (darkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900') // Inactive State
                                        }
                                    `}
                                >
                                    <div className={`flex-shrink-0 flex items-center justify-center ${isCollapsed ? 'w-full' : 'w-8 mr-3'} ${active ? 'text-current' : ''}`}>
                                        {item.icon}
                                    </div>
                                    {!isCollapsed && (
                                        <span className="font-medium whitespace-nowrap overflow-hidden transition-all duration-300">
                                            {item.label}
                                        </span>
                                    )}
                                    {isCollapsed && (
                                        <span className={`text-[10px] font-bold leading-none tracking-tight transition-all duration-300 ${active ? 'text-current' : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                                            {item.shortLabel}
                                        </span>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </nav>

            {/* Footer / Logout - Reduced padding in collapsed mode */}
            <div className={`mt-auto flex-shrink-0 border-t z-50 ${isCollapsed ? 'p-2' : 'p-4'} ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-white'}`}>
                <button
                    onClick={handleLogout}
                    className={`
                        w-full flex items-center rounded-xl transition-all duration-200 group
                        ${isCollapsed ? 'flex-col p-2 gap-1 justify-center' : 'flex-row p-3'}
                        ${darkMode
                            ? 'bg-red-900/20 text-red-400 hover:bg-red-900/40 hover:text-red-300'
                            : 'bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600'} 
                        ${isCollapsed ? 'border border-red-200/20' : 'border border-red-200/50'}
                    `}
                >
                    <div className={`flex-shrink-0 flex items-center justify-center ${isCollapsed ? 'w-full' : 'w-8 mr-3'}`}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                    </div>
                    {!isCollapsed && (
                        <span className="font-bold whitespace-nowrap overflow-hidden transition-all duration-300">
                            Logout
                        </span>
                    )}
                </button>
            </div>
        </div>
        </>
    );
};

export default LeftSidebar;
